import type React from "react";
import { Box, Typography, Grid, Paper, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { VenueFormData } from "../../../types/venue";

interface Props {
  formData: VenueFormData;
  onSubmit: () => void;
  isSubmitting: boolean;
}

// const InfoGrid = ({ data, columns = 3 }: { data: any; columns?: number }) => {
//   const theme = useTheme();
//   const colors = tokens(theme.palette.mode);
//   return (
//     <Grid container spacing={2}>
//       {Object.entries(data).map(([key, value]: [string, any]) => (
//         <Grid size={{ xs: 12, sm: 6, md: 12 / columns }} key={key}>
//           <Typography variant="body2" sx={{ color: colors.primary[300] }}>
//             {key}
//           </Typography>
//           <Typography variant="body1" fontWeight="bold">
//             {Array.isArray(value)
//               ? value.map((file: File) => file.name).join(", ") || "None"
//               : value || "N/A"}
//           </Typography>
//         </Grid>
//       ))}
//     </Grid>
//   );
// };

const InfoGrid = ({ data, columns = 3 }: { data: any; columns?: number }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Grid container spacing={4}>
      {Object.entries(data).map(([key, value]: [string, any]) => (
        <Grid size={{ xs: 12, sm: 6, md: 12 / columns }} key={key}>
          <Typography variant="h6" sx={{ color: colors.primary[300], mb: 2 }}>
            {key}
          </Typography>

          {Array.isArray(value) && value.length > 0 && value[0]?.preview ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {value.map((file: File, i: number) => (
                <Box
                  key={i}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #ccc",
                    boxShadow: 2,
                  }}
                >
                  <img
                    src={(file as any).preview}
                    alt={file.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body1" fontWeight="bold">
              {Array.isArray(value)
                ? value.map((file: File) => file.name).join(", ") || "None"
                : value || "N/A"}
            </Typography>
          )}
        </Grid>
      ))}
    </Grid>
  );
};

const AddVenueConfirmation: React.FC<Props> = ({
  formData,
  onSubmit,
  isSubmitting,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const venueInfo = {
    "Venue Name": formData.venueName,
    "Venue Type": formData.venueType,
    "Venue Capacity": formData.capacity || "N/A",
    Address: `${formData.addressOne}, ${formData.addressTwo}, ${formData.suburb}, ${formData.city}`,
  };

  const layoutInfo = formData.layouts.reduce((acc, layout, idx) => {
    acc[`Layout ${idx + 1}`] = `${layout.layoutType} - ${layout.capacity}`;
    return acc;
  }, {} as Record<string, string>);

  const contactInfo = {
    "Contact Person": `${formData.contactPersonName} ${formData.contactPersonSurname}`,
    Phone: formData.contactPersonPhone,
    Email: formData.contactPersonEmail,
  };

  const powerInfo = {
    "Power Available": formData.doesVenueHavePower,
    "Power Type": formData.powerType,
    "Power Outage Rate": formData.powerOutageRate,
    "Backup Power": formData.powerBackup,
    "Backup Capacity": formData.backupPowerCapacity,
    "Power Distance From Livestream Desk":
      formData.power_distance_from_livestream_desk,
    "Power Socket Type": formData.powerSocketType,
  };

  const internetInfo = {
    "WiFi Name": formData.wifi_name,
    "WiFi Password": formData.wifi_password,
    "Upload Speed": formData.internet_upload_speed,
    "Router Distance From Livestream Desk":
      formData.router_distance_from_livestream,
    "Router Accessibility": formData.router_accessibility,
    ISP: formData.internet_service_provider,
  };

  const paInfo = {
    "PA Available": formData.has_pa_system,
    "PA System Provider": formData.pa_system_provider,
    "PA System Distance From Livestream Desk":
      formData.pa_system_distance_from_livestream,
    "Other PA Provider": formData.other_pa_system_service_provider,
    "Provider Phone": formData.pa_system_contact_phone,
    "Provider Email": formData.pa_system_contact_email,
  };

  const photoInfo = {
    "Venue Photos": formData.uploadedFiles.venue,
    "PA System Photos": formData.uploadedFiles.paSystem,
    "Equipment Photos": formData.uploadedFiles.equipment,
    "Floor Plan Photos": formData.uploadedFiles.floorPlan,
  };

  console.log("photoInfo", photoInfo);

  const sectionStyle = {
    p: 2,
    border: `1px solid ${colors.primary[800]}`,
    borderRadius: 2,
    mb: 3,
    height: "100%",
    boxShadow: 0,
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Venue Information
            </Typography>
            <InfoGrid data={{ ...venueInfo, ...layoutInfo }} />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Contact Details
            </Typography>
            <InfoGrid data={contactInfo} />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Power Details
            </Typography>
            <InfoGrid data={powerInfo} />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Internet Information
            </Typography>
            <InfoGrid data={internetInfo} />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              PA System Information
            </Typography>
            <InfoGrid data={paInfo} />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={sectionStyle}>
            <Typography variant="subtitle1" gutterBottom>
              Uploaded Photos
            </Typography>
            <InfoGrid data={photoInfo} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddVenueConfirmation;
